import * as React from 'react';
import {useDispatch} from 'react-redux';

import {GridList} from 'shells/grid-list/grid-list';
// @ts-expect-error - imported from .js/.jsx file
import {ArchiveIcon} from 'shells/icon/svg/archive-icon';
// @ts-expect-error - imported from .js/.jsx file
import {CloneIcon} from 'shells/icon/svg/clone-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from 'shells/icon/svg/eye-icon';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';
import {ThumbnailButtonCard} from 'shells/thumbnail/thumbnail-button-card/thumbnail-button-card';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';

import type {
    FormStubFragment as FormStub,
    FormDuplicateInputFieldInput,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {useGetLimitsByType, openModalForType} from '../../limits';

import {PeanutFormPreview} from '../form-page/peanut-form-preview/peanut-form-preview';

import {EmptyState} from './empty-state';
import {FormTertiaryInfo} from './form-tertiary-info';

const CARD_WIDTH = 300;
const CARD_HEIGHT = 300;

type DuplicatingForm = {
    id: string;
    name: string;
};

type Props = {
    forms: FormStub[];
    onDeleteForm: (id: string) => Promise<any>;
    onRestoreForm: (id: string) => Promise<any>;
    onDuplicateForm: (arg1: FormDuplicateInputFieldInput) => Promise<any>;
    isLoading: boolean;
    isFiltering: boolean;
    isShowingArchived: boolean;
    error: string | null | undefined;
    locationSearch: string;
};

export function FormsGrid(props: Props) {
    const dispatch = useDispatch();
    const [duplicatingForm, setDuplicatingForm] = React.useState<
        DuplicatingForm | null | undefined
    >(null);
    const {limits} = useGetLimitsByType('FORMS');
    const [isSaving, setIsSaving] = React.useState<boolean>(false);

    const handleDuplicatingForm = React.useCallback(
        ({name, id}) => {
            if (limits && limits.canAdd) {
                setDuplicatingForm({name, id});
            } else {
                dispatch(openModalForType('FORMS'));
            }
        },
        [limits, setDuplicatingForm, dispatch]
    );

    return (
        <>
            <div>
                <GridList
                    isLoading={props.isLoading}
                    itemWidth={CARD_WIDTH}
                    loadingState={{itemHeight: CARD_HEIGHT, itemBackgroundColor: 'white'}}
                    error={props.error}
                    isFiltering={props.isFiltering || props.isShowingArchived}
                    nothingFoundState={{title: 'No forms were found'}}
                    emptyStateComponent={<EmptyState locationSearch={props.locationSearch} />}
                >
                    {props.forms && props.forms.length
                        ? props.forms.map((form) => {
                              const options = [
                                  {
                                      label: 'View',
                                      handleSelect: () => {
                                          appHistory.push(form.htmlUrlPath);
                                      },
                                      icon: <EyeIcon wrapWithDiv={false} size={18} />,
                                  },
                                  {
                                      label: 'Make a copy',
                                      handleSelect: () => {
                                          handleDuplicatingForm({name: form.name, id: form.id});
                                      },
                                      icon: <CloneIcon size={16} />,
                                  },
                              ];

                              if (form.canArchive) {
                                  options.push({
                                      label: 'Archive',
                                      // @ts-expect-error
                                      variant: 'destructive',
                                      handleSelect: () => {
                                          props.onDeleteForm(form.id);
                                      },
                                      icon: <ArchiveIcon size={16} />,
                                  });
                              }

                              if (form.isArchived) {
                                  options.push({
                                      label: 'Restore',
                                      handleSelect: () => {
                                          props.onRestoreForm(form.id);
                                      },
                                      icon: <ArchiveIcon size={16} />,
                                  });
                              }

                              return (
                                  <ThumbnailButtonCard
                                      key={form.id}
                                      title={form.name}
                                      secondaryInfo={
                                          form.lastSubmittedTime ? (
                                              <span className='flex'>
                                                  Last submitted&nbsp;
                                                  <RelativeTimestamp
                                                      datetime={form.lastSubmittedTime}
                                                  />
                                              </span>
                                          ) : (
                                              <span>This form hasn’t been submitted yet</span>
                                          )
                                      }
                                      tertiaryInfo={<FormTertiaryInfo form={form} />}
                                      onClick={() => {
                                          appHistory.push(form.htmlUrlPath);
                                      }}
                                      options={options}
                                      thumbnail={
                                          <PeanutFormPreview
                                              publicFormId={form.publicFormId}
                                              instanceId={NutshellFrontendSettings.instanceId}
                                              isNoTouchPreview={true}
                                              isThumbnail={true}
                                          />
                                      }
                                      thumbnailHeight={196}
                                  />
                              );
                          })
                        : null}
                </GridList>
            </div>
            {duplicatingForm ? (
                <SingleInputModal
                    requireValue={true}
                    buttonText='Create'
                    initialValue={`Copy of ${duplicatingForm.name}`}
                    title='Name your new form'
                    subtitle='Add a descriptive name so you can find it later'
                    placeholder={`Copy of ${duplicatingForm.name}`}
                    isSaving={isSaving}
                    onClose={() => {
                        setDuplicatingForm(null);
                    }}
                    onSave={(newName) => {
                        setIsSaving(true);
                        const nameValue = newName && newName.length ? newName : null;

                        props
                            .onDuplicateForm({wfFormId: duplicatingForm.id, copyName: nameValue})
                            .then(() => {
                                setIsSaving(false);
                                setDuplicatingForm(null);
                            });
                    }}
                />
            ) : undefined}
        </>
    );
}
